import ChevronDownIcon from "@/components/_icons/chevronDownIcon";
import { ScheduleTimeType } from "@/dto/scheduledTime";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { DateTime } from "luxon";
import { useTranslations, useLocale } from "next-intl";
import { useMemo } from "react";

export const ScheduledTimeDropdown = ({
  scheduleTime,
  setScheduleTime,
  timezone,
  timeOptions,
  isDisabled,
}: {
  scheduleTime: ScheduleTimeType;
  setScheduleTime: (newTime: ScheduleTimeType) => void;
  timezone: string;
  timeOptions: ScheduleTimeType[];
  isDisabled?: boolean;
}) => {
  const t = useTranslations("scheduledTimeDropdown");
  const locale = useLocale();

  const localizedTimeLabel = useMemo(() => {
    const timeObj = scheduleTime?.timeObject;

    if (!timeObj) return scheduleTime.displayValue;

    let dt;

    if (typeof timeObj === "string") {
      // Initially: It is a String (ISO format)
      dt = DateTime.fromISO(timeObj);
    } else if (timeObj instanceof Date) {
      // After interaction: It becomes a native Date Object
      dt = DateTime.fromJSDate(timeObj);
    } else {
      return "";
    }

    return dt
      .setZone(timezone)
      .setLocale(locale)
      .toLocaleString(DateTime.TIME_SIMPLE);
  }, [scheduleTime, timezone, locale]);

  const dropdownDisabled = isDisabled ?? timeOptions.length === 0;

  return (
    <div>
      <Listbox value={scheduleTime} onChange={setScheduleTime}>
        {({ open }) => (
          <div className="relative">
            <ListboxButton
              className="flex items-center gap-x-2 w-full leading-6 text-gray-900 enabled:hover:bg-gray-50"
              aria-label={t("time")}
              disabled={dropdownDisabled}
            >
              <input
                type="text"
                id="scheduled_time_drop"
                className="cursor-pointer peer text-[15px] leading-6 block w-full h-[52px] rounded-[4px] border border-black bg-transparent px-2 pt-6 pb-2 pr-10 text-gray-900 placeholder-transparent hover:border-black disabled:border-[#C7C7C7] disabled:text-[#C7C7C7] disabled:cursor-not-allowed tracking-wide"
                value={localizedTimeLabel}
                disabled={dropdownDisabled}
                readOnly
              />
              <label
                htmlFor="scheduled_time_drop"
                className={`pt-0.5 text-xs leading-[18px] absolute left-2 top-3 origin-[0] -translate-y-2.5 tracking-wide ${
                  dropdownDisabled
                    ? "text-[#C7C7C7] cursor-not-allowed"
                    : "text-[#727272] cursor-pointer"
                }`}
              >
                {t("time")}
              </label>
              {timeOptions.length > 1 && (
                <ChevronDownIcon
                  aria-hidden="true"
                  className={`cursor-pointer absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 transition-transform duration-300 ${
                    open ? "rotate-180" : "rotate-0"
                  }`}
                />
              )}
            </ListboxButton>
            <ListboxOptions
              transition
              className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              <div className="p-3">
                {timeOptions.map((time) => (
                  <ListboxOption
                    key={time.displayValue}
                    value={time}
                    className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50 flex-auto cursor-pointer"
                    aria-label={time.displayValue}
                  >
                    <p className="text-[15px]">{time.displayValue}</p>
                  </ListboxOption>
                ))}
              </div>
            </ListboxOptions>
          </div>
        )}
      </Listbox>
    </div>
  );
};
